import React, { useState, useRef } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Image from '../../../components/AppImage';

const PhotoUploadModal = ({ isOpen, onClose, installation, onUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (e?.type === "dragenter" || e?.type === "dragover") {
      setDragActive(true);
    } else if (e?.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setDragActive(false);
    
    if (e?.dataTransfer?.files && e?.dataTransfer?.files?.[0]) {
      handleFiles(e?.dataTransfer?.files);
    }
  };

  const handleChange = (e) => {
    e?.preventDefault();
    if (e?.target?.files && e?.target?.files?.[0]) {
      handleFiles(e?.target?.files);
    }
  };

  const handleFiles = (files) => {
    const fileArray = Array.from(files);
    const newFiles = fileArray?.map(file => ({
      id: Date.now() + Math.random(),
      file,
      preview: URL.createObjectURL(file),
      caption: '',
      name: file?.name,
      size: file?.size
    }));
    
    setUploadedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => {
      const updated = prev?.filter(f => f?.id !== fileId);
      // Clean up object URLs
      const removed = prev?.find(f => f?.id === fileId);
      if (removed) {
        URL.revokeObjectURL(removed?.preview);
      }
      return updated;
    });
  };

  const updateCaption = (fileId, caption) => {
    setUploadedFiles(prev =>
      prev?.map(f => f?.id === fileId ? { ...f, caption } : f)
    );
  };

  const handleUpload = async () => {
    if (uploadedFiles?.length === 0) return;
    
    setUploading(true);
    try {
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      const uploadedPhotos = uploadedFiles?.map(file => ({
        id: Date.now() + Math.random(),
        url: file?.preview,
        thumbnail: file?.preview,
        caption: file?.caption,
        uploadedAt: new Date()?.toISOString(),
        fileName: file?.name,
        fileSize: file?.size
      }));
      
      onUpload(uploadedPhotos);
      setUploadedFiles([]);
      onClose();
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i))?.toFixed(2)) + ' ' + sizes?.[i];
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg shadow-elevated max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Upload Installation Photos</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {installation?.storeName} - {installation?.brandName}
            </p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <Icon name="X" size={20} />
          </Button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Upload Area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              dragActive 
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <Icon name="Upload" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              Drop photos here or click to browse
            </h3>
            <p className="text-muted-foreground mb-4">
              Support for JPG, PNG, WebP files up to 10MB each
            </p>
            <Button
              variant="outline"
              onClick={() => fileInputRef?.current?.click()}
            >
              <Icon name="FolderOpen" size={16} />
              Browse Files
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleChange}
              className="hidden"
            />
          </div>

          {/* Uploaded Files */}
          {uploadedFiles?.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium text-foreground mb-4">
                Uploaded Files ({uploadedFiles?.length})
              </h3>
              <div className="space-y-4">
                {uploadedFiles?.map((file) => (
                  <div key={file?.id} className="border border-border rounded-lg p-4">
                    <div className="flex items-start gap-4">
                      <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                        <Image
                          src={file?.preview}
                          alt={file?.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <p className="font-medium text-foreground truncate">{file?.name}</p>
                            <p className="text-sm text-muted-foreground">{formatFileSize(file?.size)}</p>
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => removeFile(file?.id)}
                            className="text-muted-foreground hover:text-error"
                          >
                            <Icon name="Trash2" size={16} />
                          </Button>
                        </div>
                        <Input
                          type="text"
                          placeholder="Add a caption (optional)"
                          value={file?.caption}
                          onChange={(e) => updateCaption(file?.id, e?.target?.value)}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between p-6 border-t border-border bg-muted/30">
          <div className="text-sm text-muted-foreground">
            {uploadedFiles?.length} file{uploadedFiles?.length !== 1 ? 's' : ''} ready to upload
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="default"
              onClick={handleUpload}
              disabled={uploadedFiles?.length === 0 || uploading}
              loading={uploading}
            >
              <Icon name="Upload" size={16} />
              {uploading ? 'Uploading...' : 'Upload Photos'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoUploadModal;